import { useState, useEffect } from 'react';
import { useLocation, Navigate, useNavigate } from 'react-router-dom';
import axios from '../lib/axios';
import {
  CheckCircle2,
  Calendar,
  Clock,
  RefreshCw,
  XCircle,
  Loader2,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

export default function BookingSuccess() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  if (!state?.booking) {
    return <Navigate to="/" replace />;
  }

  const { booking, isReschedule, bookingUrl, customerId, jobId, token } = state;

  // Auto-redirect to booking confirmation page after 5 seconds
  useEffect(() => {
    if (bookingUrl) {
      const timer = setTimeout(() => {
        window.location.href = bookingUrl;
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [bookingUrl]);

  const dateStr = booking.date
    ? format(parseISO(booking.date), 'EEEE, MMMM d, yyyy')
    : booking.dateDisplay;

  const handleReschedule = () => {
    if (bookingUrl) {
      window.location.href = bookingUrl;
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    setCancelError(null);
    try {
      await axios.post('/api/booking/cancel', { customerId, jobId, sig: token });
      navigate('/book/cancelled', { state: { message: 'Your booking has been cancelled.' } });
    } catch (err) {
      setCancelError(err.response?.data?.error || 'Failed to cancel. Please try again.');
      setCancelling(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f8f6] to-brand-light flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-br from-brand-primary to-brand-secondary p-8 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjIiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4xKSIvPjwvc3ZnPg==')] opacity-20"></div>
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg relative z-10">
            <CheckCircle2 className="w-10 h-10 text-brand-primary" strokeWidth={3} />
          </div>
          <h1 className="text-2xl font-bold text-white relative z-10">
            {isReschedule ? 'Booking Rescheduled!' : 'Booking Confirmed!'}
          </h1>
          <p className="text-green-100 mt-2 relative z-10">
            {isReschedule ? 'Your appointment has been updated.' : 'Your appointment has been scheduled.'}
          </p>
        </div>

        <div className="p-8 space-y-6">
          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 bg-brand-light/30 rounded-xl border border-brand-light">
              <Calendar className="w-6 h-6 text-brand-neutral mt-0.5" />
              <div>
                <p className="text-xs font-bold text-brand-neutral uppercase tracking-wider">Date</p>
                <p className="font-semibold text-brand-dark text-lg">{dateStr}</p>
              </div>
            </div>
            <div className="flex items-start gap-4 p-4 bg-brand-light/30 rounded-xl border border-brand-light">
              <Clock className="w-6 h-6 text-brand-neutral mt-0.5" />
              <div>
                <p className="text-xs font-bold text-brand-neutral uppercase tracking-wider">Time</p>
                <p className="font-semibold text-brand-dark text-lg">{booking.time}</p>
                {booking.amcTime && <p className="text-sm text-brand-neutral mt-0.5">{booking.amcTime}</p>}
              </div>
            </div>
            <div className="text-center pt-2 space-y-2">
              <p className="text-sm text-brand-neutral bg-blue-50 text-blue-700 px-4 py-3 rounded-lg inline-block">
                You will receive a WhatsApp confirmation shortly.
              </p>
              <p className="text-xs text-brand-neutral">Redirecting to your booking page...</p>
            </div>
          </div>

          {/* Reschedule / Cancel */}
          <div className="space-y-3 pt-2">
            {bookingUrl && (
              <button
                onClick={handleReschedule}
                className="w-full bg-white border-2 border-brand-primary text-brand-primary font-bold py-3.5 px-6 rounded-2xl hover:bg-brand-light transition-all flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-5 h-5" />
                Reschedule Appointment
              </button>
            )}
            {customerId && jobId && (
              <button
                onClick={() => setShowCancelConfirm(true)}
                className="w-full bg-white border-2 border-red-300 text-red-500 font-bold py-3.5 px-6 rounded-2xl hover:bg-red-50 transition-all flex items-center justify-center gap-2"
              >
                <XCircle className="w-5 h-5" />
                Cancel Booking
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl animate-in zoom-in-95 fade-in duration-200">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-brand-dark mb-2">Cancel Booking?</h3>
              <p className="text-brand-neutral text-sm">
                Are you sure you want to cancel your appointment on {dateStr}?
              </p>
              {cancelError && (
                <p className="text-xs text-red-600 mt-2">{cancelError}</p>
              )}
            </div>
            <div className="space-y-3">
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="w-full bg-red-500 text-white font-bold py-3 px-6 rounded-xl hover:bg-red-600 transition-all flex items-center justify-center gap-2"
              >
                {cancelling ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  'Yes, Cancel Booking'
                )}
              </button>
              <button
                onClick={() => setShowCancelConfirm(false)}
                disabled={cancelling}
                className="w-full bg-gray-100 text-gray-700 font-medium py-3 px-6 rounded-xl hover:bg-gray-200 transition-all"
              >
                Keep My Booking
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
